import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
  BatchPrediction as BatchIcon,
  Create as CreateIcon,
  Preview as PreviewIcon,
  Refresh as RefreshIcon,
  SmartToy as AIIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { projectService, reelService, voiceService, templateService } from '../services/api';
import { useProjects } from '../contexts/ProjectContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { usePaywall } from '../hooks/usePaywall';
import Paywall from '../components/Paywall/Paywall';

interface Voice {
  voice_id: string;
  display_name: string;
  locale: string;
  gender: string;
  neural: boolean;
  preview_url?: string;
}

interface ScriptData {
  hook: string;
  main_content: string;
  call_to_action: string;
  hashtags: string[];
}

const ReelCreate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { currentProject } = useProjects();
  const { subscription, getRemainingReels, canCreateReel } = useSubscription();
  const { paywall, checkReelLimit, closePaywall } = usePaywall();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [scriptGenerated, setScriptGenerated] = useState(false);
  const [suggestedKeywords, setSuggestedKeywords] = useState<string[]>([]);
  const [templateName, setTemplateName] = useState<string | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    project_id: currentProject?.id || '',
    title: '',
    topic: '',
    language: 'en',
    duration: 30,
    style: 'engaging',
    tone: 'professional',
    target_audience: '',
    keywords: [] as string[],
    include_captions: true,
    include_music: true,
    music_style: 'upbeat',
    voice_id: '',
    voice_pitch: 1.0,
    voice_speed: 1.0,
    temperature: 0.7,
    batch_count: 1,
  });
  
  const [scriptData, setScriptData] = useState<ScriptData | null>(null);
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => {
    fetchProjects();
    const templateSlug = searchParams.get('template');
    if (templateSlug) {
      loadTemplate(templateSlug);
    }
  }, []);

  const loadTemplate = async (slug: string) => {
    try {
      const response = await templateService.get(slug);
      const template = response.data?.template;
      if (template) {
        setTemplateName(template.name);
        const config = template.config || {};
        setFormData((prev) => ({
          ...prev,
          style: config.script?.style || prev.style,
          tone: config.script?.tone || prev.tone,
          duration: config.script?.duration || prev.duration,
          target_audience: config.content?.targetAudience || prev.target_audience,
          keywords: config.content?.keywords || prev.keywords,
          music_style: config.audio?.musicGenre || template.default_music_genre || prev.music_style,
          include_music: true,
        }));
        enqueueSnackbar(`Template "${template.name}" loaded`, { variant: 'info' });
      }
    } catch (error) {
      console.error('Failed to load template:', error);
    }
  };

  useEffect(() => {
    if (formData.language && activeStep === 2) {
      fetchVoices();
    }
  }, [formData.language, activeStep]);

  const fetchProjects = async () => {
    try {
      const response = await projectService.list();
      setProjects(response.data);
      if (currentProject && !formData.project_id) {
        setFormData(prev => ({ ...prev, project_id: currentProject.id }));
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      enqueueSnackbar('Failed to load projects', { variant: 'error' });
    }
  };

  const fetchVoices = async () => {
    try {
      setLoading(true);
      const response = await voiceService.list(formData.language);
      // Handle both response formats
      const voiceData = response.data?.voices || response.data || [];
      setVoices(Array.isArray(voiceData) ? voiceData : []);
      
      // Auto-select recommended voice
      const recommendedResponse = await voiceService.recommended(
        formData.language,
        formData.style
      );
      // Handle both response formats for recommended voice
      const recommendedData = recommendedResponse.data?.voices || recommendedResponse.data?.voice || recommendedResponse.data;
      if (Array.isArray(recommendedData) && recommendedData.length > 0) {
        // If it's an array, take the first recommended voice
        setFormData(prev => ({ ...prev, voice_id: recommendedData[0].voice_id }));
      } else if (recommendedData && recommendedData.voice_id) {
        // If it's a single voice object
        setFormData(prev => ({ ...prev, voice_id: recommendedData.voice_id }));
      }
    } catch (error) {
      console.error('Failed to fetch voices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateStep = (step: number) => {
    switch (step) {
      case 0:
        if (!formData.project_id || !formData.title || !formData.topic) {
          enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
          return false;
        }
        return true;
      case 1:
        if (!formData.target_audience) {
          enqueueSnackbar('Please specify target audience', { variant: 'error' });
          return false;
        }
        return true;
      case 2:
        if (!formData.voice_id) {
          enqueueSnackbar('Please select a voice', { variant: 'error' });
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleGenerateScript = async () => {
    try {
      setLoading(true);
      
      // Construct prompt from form data
      const prompt = `Create a ${formData.duration}-second ${formData.style} ${formData.tone} script about "${formData.topic}" for ${formData.target_audience}. Title: "${formData.title}". ${formData.keywords.length > 0 ? `Include these keywords: ${formData.keywords.join(', ')}.` : ''}`;
      
      const response = await reelService.generateScript({
        prompt: prompt,
        title: formData.title,
        topic: formData.topic,
        language: formData.language,
        duration: formData.duration,
        style: formData.style,
        tone: formData.tone,
        target_audience: formData.target_audience,
        keywords: formData.keywords,
        temperature: formData.temperature,
      });
      
      setScriptData(response.data);
      setScriptGenerated(true);
      enqueueSnackbar('Script generated successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Failed to generate script:', error);
      enqueueSnackbar('Failed to generate script', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReel = async () => {
    // Check reel limit before creating
    if (!checkReelLimit()) {
      return; // Paywall will be shown
    }
    
    try {
      setLoading(true);
      
      // Construct prompt from form data
      const prompt = `Create a ${formData.duration}-second ${formData.style} ${formData.tone} script about "${formData.topic}" for ${formData.target_audience}. Title: "${formData.title}". ${formData.keywords.length > 0 ? `Include these keywords: ${formData.keywords.join(', ')}.` : ''}`;
      
      // Prepare reel data for backend
      const reelData = {
        prompt: prompt,
        project_id: parseInt(String(formData.project_id)),
        language: formData.language,
        voice: formData.voice_id,
        voice_pitch: formData.voice_pitch,
        voice_speed: formData.voice_speed,
        visual_source: 'ai_videos_t2v', // Default to T2V for now
        visual_config: {
          ai_provider: 'segmind',
          style: formData.style,
          quality: '480p'
        },
        platforms_to_publish: [],
        custom_caption: null,
        custom_hashtags: formData.keywords,
        scheduled_time: null
      };
      
      if (formData.batch_count > 1) {
        // Batch creation
        const response = await reelService.batchCreate({
          ...reelData,
          count: formData.batch_count,
        });
        enqueueSnackbar(`Created ${formData.batch_count} reels successfully!`, { variant: 'success' });
        navigate('/reels');
      } else {
        // Single reel creation
        const response = await reelService.create(reelData);
        enqueueSnackbar('Reel created successfully!', { variant: 'success' });
        navigate(`/reels/${response.data.id}`);
      }
    } catch (error) {
      console.error('Failed to create reel:', error);
      enqueueSnackbar('Failed to create reel', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewVoice = async () => {
    if (!formData.voice_id) return;
    
    try {
      setLoading(true);
      const response = await voiceService.preview({
        voice_id: formData.voice_id,
        text: scriptData ? scriptData.hook : "This is a preview of the selected voice.",
        language: formData.language,
        pitch: formData.voice_pitch,
        speed: formData.voice_speed,
      });
      
      // Play the audio preview
      if (response.data.preview_url) {
        const audio = new Audio(`http://localhost:5065${response.data.preview_url}`);
        audio.play().catch(err => {
          console.error('Failed to play audio:', err);
          enqueueSnackbar('Failed to play audio preview', { variant: 'error' });
        });
        enqueueSnackbar('Playing voice preview...', { variant: 'info' });
      } else {
        enqueueSnackbar('No preview URL received', { variant: 'error' });
      }
    } catch (error) {
      console.error('Failed to preview voice:', error);
      enqueueSnackbar('Failed to preview voice', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddKeyword = (event: any) => {
    if (event.key === 'Enter' && keywordInput.trim()) {
      event.preventDefault();
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(keyword => keyword !== keywordToRemove)
    }));
  };

  const handleGenerateKeywords = async () => {
    if (!formData.topic.trim()) return;
    
    setLoading(true);
    try {
      // Use the project's OpenAI key to generate keywords
      const response = await reelService.generateScript({
        title: formData.title || 'Keywords',
        topic: formData.topic,
        language: formData.language,
        duration: 30,
        style: 'keyword_generation',
        tone: 'professional',
        target_audience: formData.target_audience || 'general',
        keywords: [],
        temperature: 0.5,
        custom_prompt: `Given this reel topic: "${formData.topic}", suggest 5-8 relevant keywords or tags that would help categorize and enhance this content. Consider the target audience: ${formData.target_audience || 'general audience'}. Return only the keywords separated by commas, no explanations.`
      });
      
      if (response.data && response.data.script) {
        const keywords = response.data.script.split(',').map((k: string) => k.trim()).filter((k: string) => k.length > 0);
        setSuggestedKeywords(keywords);
        enqueueSnackbar('Keywords generated successfully!', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error generating keywords:', error);
      enqueueSnackbar('Failed to generate keywords', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    'Basic Information',
    'Content Settings',
    'Voice & Audio',
    'Review & Generate',
  ];

  return (
    <Container maxWidth="md">
      <Box py={3}>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/reels')}
            sx={{ mr: 2 }}
          >
            Back to Reels
          </Button>
          <Typography variant="h4" component="h1">
            Create New Reel
          </Typography>
        </Box>

        {templateName && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="body1">
              Using Template: <strong>{templateName}</strong>
            </Typography>
          </Paper>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>Basic Information</StepLabel>
            <StepContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Project</InputLabel>
                    <Select
                      value={formData.project_id}
                      onChange={(e) => setFormData({ ...formData, project_id: e.target.value as string })}
                      label="Project"
                    >
                      {projects && Array.isArray(projects) ? projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name}
                        </MenuItem>
                      )) : null}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Reel Title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    helperText="A descriptive title for your reel"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={3}
                    label="Topic"
                    value={formData.topic}
                    onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                    helperText="What is this reel about? Be specific."
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Language</InputLabel>
                    <Select
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      label="Language"
                    >
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="es">Spanish</MenuItem>
                      <MenuItem value="fr">French</MenuItem>
                      <MenuItem value="de">German</MenuItem>
                      <MenuItem value="it">Italian</MenuItem>
                      <MenuItem value="pt">Portuguese</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Duration</InputLabel>
                    <Select
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: Number(e.target.value) })}
                      label="Duration"
                    >
                      <MenuItem value={15}>15 seconds</MenuItem>
                      <MenuItem value={30}>30 seconds</MenuItem>
                      <MenuItem value={60}>60 seconds</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Box mt={2}>
                    <Button onClick={handleNext} variant="contained">
                      Next
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Content Settings</StepLabel>
            <StepContent>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Style</InputLabel>
                    <Select
                      value={formData.style}
                      onChange={(e) => setFormData({ ...formData, style: e.target.value })}
                      label="Style"
                    >
                      <MenuItem value="engaging">Engaging</MenuItem>
                      <MenuItem value="educational">Educational</MenuItem>
                      <MenuItem value="entertaining">Entertaining</MenuItem>
                      <MenuItem value="inspirational">Inspirational</MenuItem>
                      <MenuItem value="promotional">Promotional</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tone</InputLabel>
                    <Select
                      value={formData.tone}
                      onChange={(e) => setFormData({ ...formData, tone: e.target.value })}
                      label="Tone"
                    >
                      <MenuItem value="professional">Professional</MenuItem>
                      <MenuItem value="casual">Casual</MenuItem>
                      <MenuItem value="friendly">Friendly</MenuItem>
                      <MenuItem value="authoritative">Authoritative</MenuItem>
                      <MenuItem value="humorous">Humorous</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Target Audience"
                    value={formData.target_audience}
                    onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                    helperText="Who is this reel for? e.g., Young professionals, Students, Parents"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                    <TextField
                      fullWidth
                      label="Keywords"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={handleAddKeyword}
                      helperText="Press Enter to add keywords"
                      InputProps={{
                        startAdornment: formData.keywords.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mr: 1 }}>
                            {formData.keywords.map((keyword) => (
                              <Chip
                                key={keyword}
                                label={keyword}
                                size="small"
                                onDelete={() => handleRemoveKeyword(keyword)}
                              />
                            ))}
                          </Box>
                        ),
                      }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={handleGenerateKeywords}
                      disabled={!formData.topic.trim() || loading}
                      sx={{ mb: 1, whiteSpace: 'nowrap' }}
                    >
                      AI Generate
                    </Button>
                  </Box>
                  {suggestedKeywords.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        AI Suggested Keywords (click to add):
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {suggestedKeywords.map((keyword) => (
                          <Chip
                            key={keyword}
                            label={keyword}
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                              if (!formData.keywords.includes(keyword)) {
                                setFormData(prev => ({
                                  ...prev,
                                  keywords: [...prev.keywords, keyword]
                                }));
                              }
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Typography gutterBottom>AI Creativity</Typography>
                  <Slider
                    value={formData.temperature}
                    onChange={(e, value) => setFormData({ ...formData, temperature: value as number })}
                    min={0}
                    max={1}
                    step={0.1}
                    marks={[
                      { value: 0, label: 'Focused' },
                      { value: 0.5, label: 'Balanced' },
                      { value: 1, label: 'Creative' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <FormHelperText>
                    Lower values produce more focused content, higher values more creative variations
                  </FormHelperText>
                </Grid>
                
                <Grid item xs={12}>
                  <Box mt={2}>
                    <Button onClick={handleBack} sx={{ mr: 1 }}>
                      Back
                    </Button>
                    <Button onClick={handleNext} variant="contained">
                      Next
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Voice & Audio</StepLabel>
            <StepContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Voice</InputLabel>
                    <Select
                      value={formData.voice_id}
                      onChange={(e) => setFormData({ ...formData, voice_id: e.target.value })}
                      label="Voice"
                      disabled={loading}
                    >
                      {voices.map((voice) => (
                        <MenuItem key={voice.voice_id} value={voice.voice_id}>
                          {voice.display_name} - {voice.gender} ({voice.locale})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    onClick={handlePreviewVoice}
                    disabled={!formData.voice_id || loading}
                  >
                    Preview Voice
                  </Button>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography gutterBottom>Voice Pitch</Typography>
                  <Slider
                    value={formData.voice_pitch}
                    onChange={(e, value) => setFormData({ ...formData, voice_pitch: value as number })}
                    min={0.5}
                    max={2}
                    step={0.1}
                    marks={[
                      { value: 0.5, label: 'Low' },
                      { value: 1, label: 'Normal' },
                      { value: 2, label: 'High' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <Typography gutterBottom>Voice Speed</Typography>
                  <Slider
                    value={formData.voice_speed}
                    onChange={(e, value) => setFormData({ ...formData, voice_speed: value as number })}
                    min={0.5}
                    max={2}
                    step={0.1}
                    marks={[
                      { value: 0.5, label: 'Slow' },
                      { value: 1, label: 'Normal' },
                      { value: 2, label: 'Fast' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.include_captions}
                        onChange={(e) => setFormData({ ...formData, include_captions: e.target.checked })}
                      />
                    }
                    label="Include Captions"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.include_music}
                        onChange={(e) => setFormData({ ...formData, include_music: e.target.checked })}
                      />
                    }
                    label="Include Background Music"
                  />
                </Grid>
                
                {formData.include_music && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Music Style</InputLabel>
                      <Select
                        value={formData.music_style}
                        onChange={(e) => setFormData({ ...formData, music_style: e.target.value })}
                        label="Music Style"
                      >
                        <MenuItem value="upbeat">Upbeat</MenuItem>
                        <MenuItem value="calm">Calm</MenuItem>
                        <MenuItem value="corporate">Corporate</MenuItem>
                        <MenuItem value="epic">Epic</MenuItem>
                        <MenuItem value="minimal">Minimal</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Box mt={2}>
                    <Button onClick={handleBack} sx={{ mr: 1 }}>
                      Back
                    </Button>
                    <Button onClick={handleNext} variant="contained">
                      Next
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Review & Generate</StepLabel>
            <StepContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Reel Summary
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Title</Typography>
                          <Typography variant="body1">{formData.title}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Language</Typography>
                          <Typography variant="body1">{formData.language.toUpperCase()}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Duration</Typography>
                          <Typography variant="body1">{formData.duration} seconds</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Style</Typography>
                          <Typography variant="body1">{formData.style}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">Topic</Typography>
                          <Typography variant="body1">{formData.topic}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                
                {!scriptGenerated && (
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                      onClick={handleGenerateScript}
                      disabled={loading}
                    >
                      {loading ? 'Generating Script...' : 'Generate AI Script'}
                    </Button>
                  </Grid>
                )}
                
                {scriptGenerated && scriptData && (
                  <>
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Generated Script
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>Hook:</Typography>
                          <Typography variant="body2" paragraph>{scriptData.hook}</Typography>
                          
                          <Typography variant="subtitle2" gutterBottom>Main Content:</Typography>
                          <Typography variant="body2" paragraph>{scriptData.main_content}</Typography>
                          
                          <Typography variant="subtitle2" gutterBottom>Call to Action:</Typography>
                          <Typography variant="body2" paragraph>{scriptData.call_to_action}</Typography>
                          
                          <Typography variant="subtitle2" gutterBottom>Hashtags:</Typography>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {scriptData?.hashtags?.map((tag) => (
                              <Chip key={tag} label={tag} size="small" />
                            )) || <Typography variant="body2" color="text.secondary">No hashtags generated</Typography>}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Button
                          variant="outlined"
                          startIcon={<RefreshIcon />}
                          onClick={handleGenerateScript}
                          disabled={loading}
                        >
                          Regenerate Script
                        </Button>
                        
                        <TextField
                          type="number"
                          label="Batch Count"
                          value={formData.batch_count}
                          onChange={(e) => setFormData({ ...formData, batch_count: parseInt(e.target.value) || 1 })}
                          inputProps={{ min: 1, max: 10 }}
                          sx={{ width: 120 }}
                        />
                        
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={loading ? <CircularProgress size={20} /> : formData.batch_count > 1 ? <BatchIcon /> : <CreateIcon />}
                          onClick={handleCreateReel}
                          disabled={loading}
                          sx={{ flex: 1 }}
                        >
                          {loading ? 'Creating...' : `Create ${formData.batch_count > 1 ? formData.batch_count + ' Reels' : 'Reel'}`}
                        </Button>
                      </Box>
                    </Grid>
                  </>
                )}
                
                <Grid item xs={12}>
                  <Box mt={2}>
                    <Button onClick={handleBack} disabled={loading}>
                      Back
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </StepContent>
          </Step>
        </Stepper>
      </Box>

      {/* Paywall Dialog */}
      {paywall && (
        <Paywall
          open={true}
          onClose={closePaywall}
          feature={paywall.feature}
          description={paywall.description}
          requiredPlan={paywall.requiredPlan}
        />
      )}
    </Container>
  );
};

export default ReelCreate;